import React, { useState } from 'react'
import styled from 'styled-components'
import Tooltip from '~/components/core/Tooltip'
import { HTMLAttributePropsBase } from '~/types/HTMLAttributePropsBase'

interface ImageIconWrapperProps {
  cursor: string
  flexShrink?: number
  marginRight?: number | string
  hovered?: boolean
}

interface StyledSVGProps {
  icon?: SVGAsset
  width?: number | string
  height?: number | string
  opacity?: number
  backgroundSize?: string
}

const StyledSvg = styled.svg<StyledSVGProps>`
  display: flex;
  background-image: url(${(props) => props.icon});
  background-repeat: no-repeat;
  background-position: center;
  background-size: ${(props) =>
    props.backgroundSize ? props.backgroundSize : 'contain'};
  width: ${(props) => (props.width ? props.width : 30)}px;
  height: ${(props) => (props.height ? props.height : 30)}px;
  opacity: ${(props) => (props.opacity ? props.opacity : 1)};
`

const ImageIconWrapper = styled.div<ImageIconWrapperProps>`
  display: flex;
  margin-right: ${(props) => (props.marginRight ? props.marginRight : '')}px;
  flex-shrink: ${(props) => (props.flexShrink ? props.flexShrink : 0)};
  cursor: ${(props) => props.cursor};
  align-items: center;
  justify-content: center;
`

/**
 * Properties for the ImageIcon component.
 *
 * @interface ImageIconProps
 * @extends {HTMLAttributePropsBase}
 */
interface ImageIconProps {
  /** The primary SVG icon. */
  icon?: SVGAsset

  /** The SVG icon to show on hover. */
  hoverIcon?: SVGAsset

  /** Indicates if the cursor should change on hover. */
  cursor?: boolean

  /** The flex-shrink value for the icon. */
  flexShrink?: number

  /** Width of the icon. */
  width?: number | string

  /** Height of the icon. */
  height?: number | string

  /** Opacity of the icon. */
  opacity?: number

  /** Size of the icon's background. */
  backgroundSize?: string

  /** Callback function when the icon is clicked. */
  onClick?: () => void

  /** Margin to the right of the icon. */
  marginRight?: number | string

  /** Tooltip text to show on hover. */
  tooltipText?: string

  /** Children elements. */
  children?: React.ReactElement

  /** Ref callback for the wrapping element. */
  wrapperRef?: (element: Element) => void

  /** Ref callback for the SVG element. */
  svgRef?: (element: Element) => void

  /** Custom styles for the icon. */
  style?: React.CSSProperties

  /** @deprecated The primary SVG icon URL. */
  url?: SVGAsset

  /** @deprecated Absolute path or SVG asset for the icon. */
  absoluteIconPath?: string | SVGAsset

  /** @deprecated The SVG icon URL to show on hover. */
  hoverUrl?: string

  /** @deprecated Absolute path for the hover icon. */
  absoluteHoverIconPath?: string
}
interface ImageIconProps extends HTMLAttributePropsBase {
  icon?: SVGAsset
  hoverIcon?: SVGAsset
  cursor?: boolean
  flexShrink?: number
  width?: number | string
  height?: number | string
  opacity?: number
  backgroundSize?: string
  onClick?: () => void
  marginRight?: number | string
  tooltipText?: string
  children?: React.ReactElement
  wrapperRef?: (element: Element) => void
  svgRef?: (element: Element) => void
  style?: React.CSSProperties

  //deprecated
  url?: SVGAsset
  absoluteIconPath?: string | SVGAsset
  hoverUrl?: string
  absoluteHoverIconPath?: string
}

const ImageIcon = ({
  id,
  className,
  'data-testid': dataTestId,
  title,
  role,
  icon,
  hoverIcon,
  cursor,
  flexShrink,
  width,
  height,
  opacity,
  backgroundSize: clip,
  onClick,
  marginRight,
  tooltipText,
  wrapperRef,
  svgRef,

  //deprecated! see jsdoc for ImageIconProps
  url,
  absoluteIconPath,
  hoverUrl,
  absoluteHoverIconPath,
}: ImageIconProps) => {
  const [hovered, setHovered] = useState(false)

  const getUrl = () => {
    if (icon && (!hoverIcon || !hovered)) {
      return icon
    }

    if (hoverIcon && hovered) {
      return hoverIcon
    }

    //fallback to deprecated handler
    const relativePrefix = '~/assets/icons-light-theme/'
    if ((hoverUrl || absoluteHoverIconPath) && hovered) {
      return (
        absoluteHoverIconPath ?? `${relativePrefix}${hoverUrl ?? 'no-icon.svg'}`
      )
    }

    return (absoluteIconPath ??
      `${relativePrefix}${url ?? 'no-icon.svg'}`) as string
  }
  const resolvedUrl = getUrl()
  return (
    <>
      <ImageIconWrapper
        id={id}
        className={className}
        title={title}
        data-testid={dataTestId}
        role={role}
        cursor={cursor ? 'pointer' : 'inherit'}
        flexShrink={flexShrink}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        marginRight={marginRight}
        ref={(ref) => {
          if (!wrapperRef) return undefined

          wrapperRef(ref as Element)
        }}
      >
        <StyledSvg
          cursor={cursor ? 'pointer' : 'inherit'}
          icon={resolvedUrl}
          ref={(ref) => {
            if (!svgRef) return

            svgRef(ref as Element)
          }}
          width={width}
          height={height}
          opacity={opacity}
          backgroundSize={clip}
        />
      </ImageIconWrapper>
      {tooltipText && <Tooltip id={tooltipText} />}
    </>
  )
}

export type { ImageIconProps }
export { ImageIcon }
